<template>
  <div>
    <div class="columns is-justify-content-space-between" v-if="!detail">
      <selecteur-action @handleAction="handleAction" class="column is-narrow"
                        :options='actions'
                        :placeholder='$t("general.placeHolder.groupedAction")'
                        :titleButton="'general.button.apply'"
                        :elementsSelectionnes="getAllObjectifSelected().length > 0">
      </selecteur-action>
      <div class="column is-narrow">
        <div class="button is-addOrEdit" @click="addObjectif()">
          {{ $t('produitSalon.objectifsVente.actions.add') }}
        </div>
      </div>
    </div>
    <div v-else class="columns is-justify-content-end is-align-items-end">
      <div class="column" v-if="produit.regroupementProduit && showMsgRegroupementProduit">
        <msg-info-produit :regroupementProduit="produit.regroupementProduit"/>
      </div>
      <div class="column is-narrow">
        <exportExcelButton
          :perm="permissions.exporter"
          :query="exportExcelQuery"/>
      </div>
    </div>
    <v-client-table v-model="produit.objectifsVente" :columns="columns" :options="options"
                    ref="objectifsVenteArray">
      <div slot="selected" slot-scope="props">
        <b-checkbox :value="props.row.selected" class="mgt-5px"
                    @input="updateValue(props.index, 'selected', $event)">
        </b-checkbox>
      </div>
      <div slot="annee" slot-scope="props" class="is-pulled-left width-100percent">
        <b-input v-model="props.row.annee" type="number" v-if="!detail"
                 step="0.01"
                 @input="updateValue(props.index, 'annee', $event)"></b-input>
        <span v-else>{{ props.row.annee }}</span>
      </div>
      <div slot="montantHt" slot-scope="props" class="is-pulled-left width-100percent">
        <b-input v-model="props.row.montantHt" type="number" expanded  v-if="!detail"
                 step="0.01"
                 @input="updateValue(props.index, 'montantHt', $event)"></b-input>
        <span v-else>{{ props.row.montantHt | formatDevise }}</span>
      </div>
      <div slot="commercial" slot-scope="props" class="is-pulled-left width-100percent">
        <user-selector
          v-if="!detail"
          v-model="props.row.commercialId"
          @input="updateValue(props.index, 'commercialId', $event)"
          :critere="(u) => [constantes.COMMERCIAL, constantes.DIRECTEUR_COMMERCIAL]
            .includes(u.profil)"
        />
        <span v-else> {{ getCommercialById(props.row.commercialId) }}</span>
      </div>
      <div slot="delete" slot-scope="props">
        <a @click="deleteObjectif(props.index)">
          <b-icon class="mgt-5px" icon="delete-forever" type="is-danger"></b-icon>
        </a>
      </div>
    </v-client-table>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import {
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
  SORT_ICON,
  TEXTS,
} from '@/configuration/clientTableConf';
import selecteurAction from '@/components/general/selecteurAction';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import MsgInfoProduit from '@/components/presse/regroupementProduit/informations/MsgInfoProduit';
import userSelector from '@/components/commun/user/userSelector';
import {
  ACTION_DUPLICATION,
  ACTION_SUPPRESSION,
} from '@/configuration/presse/produitSalon/produitSalonConfiguration';
import { EXPORT_EXCEL_OBJECTIF_VENTE } from '@/configuration/presse/produitPresse/produitPresseConf';
import { createNamespacedHelpers } from 'vuex';
import messageErreur from '@/services/general/messageErreur';
import { columnsSplice } from '@/services/general/columns';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import {
  COMMERCIAL,
  DIRECTEUR_COMMERCIAL,
} from '@/configuration/parametrage/utilisateurConf';

const Parametres = createNamespacedHelpers('parametres');

export default {
  props: {
    produit: {
      type: Object,
      required: true,
    },
    detail: {
      type: Boolean,
      required: false,
    },
    showMsgRegroupementProduit: {
      type: Boolean,
    },
    permissions: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      actions: [
        { action: ACTION_DUPLICATION, translation: 'produitSalon.objectifsVente.actions.dupliquer' },
        { action: ACTION_SUPPRESSION, translation: 'produitSalon.objectifsVente.actions.supprimer' },
      ],
      constantes: {
        COMMERCIAL,
        DIRECTEUR_COMMERCIAL,
      },
    };
  },
  components: {
    selecteurAction,
    MsgInfoProduit,
    userSelector,
    exportExcelButton,
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    getCommercial() {
      return this.parametre(PARAMETRES_KEYS.utilisateur)
        .filter((user) => user.profil === COMMERCIAL || user.profil === DIRECTEUR_COMMERCIAL)
        .sort((a, b) => {
          if (a.decorator.nomPrenom < b.decorator.nomPrenom) return -1;
          if (a.decorator.nomPrenom > b.decorator.nomPrenom) return 1;
          return 0;
        });
    },
    allObjectifSelected() {
      return this.getAllObjectifSelected().length === this.produit.objectifsVente.length;
    },
    iconSelectTotal() {
      if (this.allObjectifSelected) {
        return 'checkbox-multiple-marked-outline';
      }
      return 'checkbox-multiple-blank-outline';
    },
    columns() {
      const columns = ['selected', 'annee', 'commercial', 'montantHt', 'delete'];
      if (this.detail) {
        columnsSplice(columns, 'selected');
        columnsSplice(columns, 'delete');
      }
      return columns;
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          annee: this.$t('produitSalon.objectifsVente.tab.anne'),
          montantHt: this.$t('produitSalon.objectifsVente.tab.montantHt'),
          commercial: this.$t('produitSalon.objectifsVente.tab.commercial'),
          delete: '',
          selected: (h) => {
            return h(
              'a', {
                attrs: {
                  class: 'pointer',
                },
                on: {
                  click: () => {
                    const allSelected = this.allObjectifSelected;
                    this.produit.objectifsVente.forEach(item => {
                      item.selected = !allSelected;
                    });
                  },
                },
              },
              [
                h('b-icon', {
                  attrs: {
                    icon: this.iconSelectTotal,
                  },
                }),
              ],
            );
          },
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: texts,
        columnsClasses: {
          delete: 'colonneSelected',
          selected: 'colonneSelected',
        },
        sortable: this.detail ? ['annee', 'commercial', 'montantHt'] : [],
      };
    },
    exportExcelQuery() {
      return {
        typeDonnee: EXPORT_EXCEL_OBJECTIF_VENTE,
        typeExport: null,
        filtres: {
          query: { produitId: this.produit.id },
        },
      };
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametreUtilisateur: 'loadParametreUtilisateur',
    }),
    getAllObjectifSelected() {
      return this.produit.objectifsVente.filter((el) => {
        return el.selected;
      });
    },
    getCommercialById(commercialId) {
      if (!commercialId) return '';
      const commercial = this.getCommercial.find((commercial) => {
        return commercial.id === commercialId;
      });
      if (commercial) return commercial.decorator.nomPrenom;
      return '';
    },
    handleAction(type) {
      switch (type) {
        case ACTION_SUPPRESSION:
          this.produit.objectifsVente = this.produit.objectifsVente
            .filter((el) => !el.selected);
          break;
        case ACTION_DUPLICATION:
          this.duplicationObjectif();
          break;
        default:
          break;
      }
    },
    addObjectif() {
      this.produit.objectifsVente.push({
        annee: null,
        commercialId: null,
        montantHt: null,
        selected: false,
      });
    },
    deleteObjectif(index) {
      this.produit.objectifsVente.splice(index - 1, 1);
    },
    updateValue(index, variable, value) {
      this.produit.objectifsVente[index - 1][variable] = value;
    },
    duplicationObjectif() {
      const newObjectif = cloneDeep(this.produit.objectifsVente.filter((el) => {
        return el.selected;
      }));
      newObjectif.forEach((el) => {
        el.selected = false;
        if (el.id) delete el.id;
      });
      this.produit.objectifsVente = this.produit.objectifsVente.concat(newObjectif);
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametreUtilisateur();
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>

<style scoped>

</style>
